'use client';

import { Pickup as IconPickup } from '@ahamove/icons/map';
import { IndexedPath as IconIndexedPath } from '@ahamove/icons/marker';
import type { Address, Path } from '@ahm/api-wrappers-core';
import {
  ActionIcon,
  Group,
  rem,
  Space,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { ArrowLeft, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { AddressAutocomplete } from '@/components/address-autocomplete';
import { publicEnv } from '@/config/public-env.config.mjs';
import { ActionIndexPath } from '@/lib/constants';

type ModalMapAutocompleteProps = {
  // custom props here
  isPickup?: boolean;
  // initialAddress?: Address;
  onAddressChange: (address: Address | null) => void;
  onClose: () => void;
  pathIndex: number;
  path?: Path;
  isBackBtn?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function ModalMapAutocomplete(props: ModalMapAutocompleteProps) {
  const {
    isPickup,
    // initialAddress,
    onAddressChange,
    onClose,
    pathIndex = ActionIndexPath.ShowInput,
    isBackBtn = false,
  } = props;
  const { colorScheme } = useMantineColorScheme();
  const tBooking = useTranslations('booking');
  const curLocale = useLocale();

  return (
    <>
      <Group
        justify="space-between"
        w="100%"
        px={6}
        bg="white"
        style={{
          zIndex: 11,
          border: `1px solid var(--mantine-color-neutral-0)`,
        }}
      >
        <ActionIcon
          size={44}
          variant="transparent"
          onClick={onClose}
          style={{ outline: 'none !important' }}
        >
          {isBackBtn ? (
            <ArrowLeft
              size={24}
              color={
                colorScheme === 'dark'
                  ? 'var(--mantine-color-neutral-2)'
                  : 'var(--mantine-color-neutral-6)'
              }
            />
          ) : (
            <X
              size={24}
              color={
                colorScheme === 'dark'
                  ? 'var(--mantine-color-neutral-2)'
                  : 'var(--mantine-color-neutral-6)'
              }
            />
          )}
        </ActionIcon>
        <Title order={4}>
          {tBooking(
            isPickup ? 'location.pickup_point' : 'location.delivery_point'
          )}
        </Title>
        <Space w={44} />
      </Group>
      <AddressAutocomplete
        placeholder={tBooking(
          isPickup
            ? 'location.where_do_you_want_to_pickup'
            : 'location.where_do_you_send'
        )}
        pointType={isPickup ? 'pickup' : 'dropoff'}
        withSavedPlace
        isMobile
        inputProps={{
          leftSection: isPickup ? (
            <IconPickup />
          ) : (
            <IconIndexedPath
              value={pathIndex && pathIndex > 0 ? pathIndex : undefined}
            />
          ),
          leftSectionWidth: rem(48),

          styles: {
            wrapper: {
              background: 'white',
              borderBottom: `1px solid ${isPickup ? 'var(--mantine-color-secondary-6)' : 'var(--mantine-color-primary-6)'}`,
              boxShadow: `rgba(0, 0, 0, 0.12) 0px 8px 32px`,
              zIndex: 11,
            },
            input: {
              width: '100%',
              minHeight: rem(64),
              fontSize: rem(18),
              lineHeight: rem(24),
              fontWeight: 700,

              outline: 'none',
              borderColor: 'transparent',
            },
          },
        }}
        lang={curLocale}
        apiURL={publicEnv.NEXT_PUBLIC_EP_API_URI}
        apiKey={publicEnv.NEXT_PUBLIC_EP_API_KEY}
        opened
        onChange={onAddressChange}
      />
    </>
  );
}
